import { set, unset } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'
import { isAndroid, isIpad } from '../utils/os'

export interface BridgeParams {
  arguments?: any,
  callbackName?: string,
  trigger?: (error: VideoErrorInfo, data: any) => void,
  conventionTrigger?: {
    [methodName: string]: () => void,
  },
}

export type PromiseExecutor<T> = (
  resolve: (value: T | PromiseLike<T>) => void,
  reject: (reason?: any) => void
) => void

export interface VideoErrorInfo {
  errorCode: number,
  message: string,
}

const geneWrapCall = (call: any) => (errorString: string, dataString: string) => {
  const error = errorString ? JSON.parse(errorString) : undefined
  const data = dataString ? JSON.parse(dataString) : undefined
  call?.(error, data)
}

const getMethodName = (methodName: string) => {
  const name = `webBridgeApi.${methodName}.callback`
  return `${name}_${uuidv4()}}`
}

export const bridgeCall = (methodName: string, params?: BridgeParams, finalName?: string) => {
  if (params?.trigger) {
    const wrapCall = geneWrapCall(params.trigger)
    const name = finalName || getMethodName(methodName)
    set(window, name, wrapCall)
    // @ts-ignore
    params.trigger = name
  }
  if (params?.conventionTrigger) {
    Object.keys(params.conventionTrigger).forEach((callbackName) => {
      const wrapCall = geneWrapCall(params?.conventionTrigger?.[callbackName])
      set(window, `webBridgeApi.${callbackName}`, wrapCall)
    })
  }
  if (isIpad) {
    // @ts-ignore
    window?.webkit?.messageHandlers?.[methodName]?.postMessage?.(JSON.stringify(params))
  } else if (isAndroid) {
    // @ts-ignore
    window?.RecorderWebView?.action(methodName, JSON.stringify(params))
  } else {
    // @ts-ignore
    window?.electron?.messageHandlers?.(methodName, JSON.stringify(params))
  }
}

/**
 * bridge调用的异步方法，约定原生方法的结果都会通过onResult事件来传递
 */
export const bridgeCallAsync = <T>(
  methodName: string,
  params: BridgeParams = {},
  judgeFunc?: PromiseExecutor<T>,
) => new Promise<T>((resolve, reject) => {
  const originalCall = params?.trigger
  const finalName = getMethodName(methodName)
  // @ts-ignore
  params.trigger = (error: VideoErrorInfo, data: T) => {
    originalCall?.(error, data)
    unset(window, finalName)
    if (typeof judgeFunc === 'function') {
      judgeFunc(resolve, reject)
    } else if (error) {
      reject(error)
    } else {
      resolve(data)
    }
  }
  bridgeCall(methodName, params, finalName)
})

/**
 * 监听外部主动触发的事件
 * NOTE: 目前同一个事件只支持最后注册进来的回调，以后有时间再维护队列支持
 */
export const registerBridge = (
  methodName: string,
  callback: (err: any, data: any) => void,
) => {
  const wrapCall = geneWrapCall(callback)
  const name = `webBridgeApi.${methodName}.callback`
  set(window, name, wrapCall)
}
